import PairWithADifferentCoachButton from "@/Components/ClientCoachInfo/CoachInfo/Parts/PairWithADifferentCoachButton";
import PrimaryButton from "@/Components/UI/Forms/PrimaryButton";
import SecondaryButton from "@/Components/UI/Forms/SecondaryButton";
import { LoadsClientAttributesWithIndustryAndIndustryJob } from "@/types/clientAttributes";
import { LoadsCoachDetails } from "@/types/coachDetails";
import { LoadsIndustriesWithPivotData } from "@/types/industry";
import { LoadsIndustryJobsWithPivotData } from "@/types/industryJob";
import { LoadsLanguages } from "@/types/language";
import { LoadsLifeAreas } from "@/types/lifeArea";
import { LoadsMatchingAttributes } from "@/types/matchingAttribute";
import { LoadsMatchingProfile } from "@/types/matchingProfile";
import { Client, Coach } from "@/types/page";
import { LoadsProfessionalPositionWithIndustry } from "@/types/professionalPosition";
import { LoadsReviews } from "@/types/review";
import classNames from "classnames";
import { ReactNode, useEffect, useState } from "react";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import { DetailsSection } from "./DetailsSections";
import CoachInfoDetails from "./Parts/CoachInfoDetails";
import CoachInfoHeader from "./Parts/CoachInfoHeader";

export type CoachInfoData = {
    coach: Coach &
        LoadsCoachDetails &
        LoadsLifeAreas &
        LoadsReviews &
        LoadsMatchingAttributes &
        LoadsIndustriesWithPivotData &
        LoadsIndustryJobsWithPivotData &
        LoadsProfessionalPositionWithIndustry &
        LoadsMatchingProfile &
        LoadsLanguages;
    client?: Client &
        LoadsClientAttributesWithIndustryAndIndustryJob &
        LoadsLifeAreas;
    uniqueJobTitlesCoached: string[];
    appointmentReviewStats: {
        averageRating: number | null;
        totalReviews: number;
    };
    number_of_clients_coached: number;
};

export type TopItemsAndOptions =
    | "license"
    | "location"
    | "ratings"
    | "viewAvailability"
    | "submitRating";

export type CoachInfoSubcomponentProps = {
    coachInfoData: CoachInfoData | null;
    config: {
        bigCenteredProfilePhoto: boolean | null;
        hideDetailsInSeeMore: boolean;
        topItemsAndOptions: TopItemsAndOptions[];
        /** Rendered inline with the coach's name, wrapping beneath it when there is no room. */
        nameBadge?: ReactNode;
        switchCoachButton?: () => void;
        switchCoachButtonLabel?: string;
        /** Pass null to hide the subtext entirely; undefined keeps the default copy. */
        switchCoachButtonSubtext?: string | null;
        switchCoachButtonVariant: "primary" | "secondary";
        showPairWithADifferentCoachButton: boolean;
        sectionsToShowOutsideOfSeeMore: DetailsSection[];
    };
};

type CoachInfoProps = {
    coachId: number;
    config?: Partial<CoachInfoSubcomponentProps["config"]>;
};

export default function CoachInfo(props: CoachInfoProps) {
    const configWithDefaults = {
        bigCenteredProfilePhoto: null,
        hideDetailsInSeeMore: false,
        topItemsAndOptions: ["license"] as TopItemsAndOptions[],
        nameBadge: undefined,
        switchCoachButton: undefined,
        switchCoachButtonLabel: undefined,
        switchCoachButtonSubtext: undefined,
        switchCoachButtonVariant: "primary" as const,
        showPairWithADifferentCoachButton: false,
        sectionsToShowOutsideOfSeeMore: [],
        ...props.config,
    };

    const [coachInfoData, setCoachInfoData] = useState<CoachInfoData | null>(
        null,
    );
    const [seeMore, setSeeMore] = useState(
        !configWithDefaults.hideDetailsInSeeMore,
    );

    const getCoachInfoData = async () => {
        setCoachInfoData(null);
        const { data } = await window.axios.get<CoachInfoData>(
            route("coachInfo", props.coachId),
        );
        setCoachInfoData(data);
    };

    useEffect(() => {
        getCoachInfoData();
    }, [props.coachId]);

    return (
        <div
            data-testid="user-info"
            className="@container/coach-info flex flex-col bg-white px-4 py-4 text-brand-black lg:rounded-2xl"
        >
            <CoachInfoHeader
                config={configWithDefaults}
                coachInfoData={coachInfoData}
            />
            {configWithDefaults.switchCoachButton && (
                <div className="flex flex-col items-center justify-center gap-2 mt-8 mb-2">
                    {configWithDefaults.switchCoachButtonVariant ===
                    "secondary" ? (
                        <SecondaryButton
                            className="w-full @sm/coach-info:w-3/4 self-center justify-center py-3"
                            onClick={() =>
                                configWithDefaults.switchCoachButton?.()
                            }
                        >
                            {configWithDefaults.switchCoachButtonLabel ??
                                `Start coaching with ${coachInfoData?.coach?.first_name ?? "..."}`}
                        </SecondaryButton>
                    ) : (
                        <PrimaryButton
                            className="w-full @sm/coach-info:w-3/4 self-center justify-center py-3"
                            onClick={() =>
                                configWithDefaults.switchCoachButton?.()
                            }
                        >
                            {configWithDefaults.switchCoachButtonLabel ??
                                `Start coaching with ${coachInfoData?.coach?.first_name ?? "..."}`}
                        </PrimaryButton>
                    )}
                    {configWithDefaults.switchCoachButtonSubtext !== null && (
                        <div className="text-sm text-brand-gray">
                            {configWithDefaults.switchCoachButtonSubtext ??
                                "You can switch to a different coach at any time"}
                        </div>
                    )}
                </div>
            )}
            <CoachInfoDetails
                inSeeMore={false}
                config={configWithDefaults}
                coachInfoData={coachInfoData}
            />
            {configWithDefaults.hideDetailsInSeeMore && (
                <button
                    type="button"
                    className={classNames(
                        "font-regular flex cursor-pointer items-center justify-center  w-full text-base mt-1 text-brand-gray",
                        configWithDefaults.bigCenteredProfilePhoto
                            ? ""
                            : "@sm/coach-info:justify-start @sm/coach-info:mt-4",
                    )}
                    onClick={() => setSeeMore(!seeMore)}
                    aria-expanded={seeMore}
                    aria-controls="coach-details"
                >
                    <div className="mr-1 ">
                        {seeMore ? "See less" : "See more"}
                    </div>
                    {seeMore ? (
                        <IoChevronUp className="mt-1" />
                    ) : (
                        <IoChevronDown className="mt-1" />
                    )}
                </button>
            )}
            {seeMore && (
                <div id="coach-details">
                    <CoachInfoDetails
                        inSeeMore={true}
                        config={configWithDefaults}
                        coachInfoData={coachInfoData}
                    />
                </div>
            )}
            {configWithDefaults.showPairWithADifferentCoachButton && (
                <PairWithADifferentCoachButton />
            )}
        </div>
    );
}
